import { ShipmentTrackerStatus } from "../fulfillments/outbound-shipment-label";
import { ReturnStatus } from "../return-status";
import {
  FraudRiskTag,
  LabelCreationFailedTag,
  OutOfStockTag,
  RefundPaymentFailedTag,
  RestockFailedTag,
  TRACKING_EXCEPTION_TAG_NAMES,
} from "../return-tags";
import { SortDirection, TableSort } from "../tables/table";
import { ArrayStringFilterOperator } from "./advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "./advanced-filters/boolean-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
  RelativeTimeUnit,
} from "./advanced-filters/date-filter";
import { AdvancedFilterType } from "./advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "./advanced-filters/number-filter";
import { DEFAULT_PAGE_SIZE, viewTypes } from "./views";

export enum ReturnTableColumns {
  ORDER = "order",
  CUSTOMER_NAME = "customer_name",
  ZIP_CODE = "zip_code",
  SHIPMENTS = "shipments",
  CREATED_AT = "created_at",
  TYPE = "type",
  STATUS = "status",
  RISK = "risk",
  ITEMS_COUNT = "items_count",
  DOLLAR_VALUE = "dollar_value",
  TAGS = "tags",
  EXTERNAL_ID = "external_id",
  SHIPPING_COUNTRY = "shipping_country",
}

// Default columns for the returns and claims tables
const defaultReturnColumns = [
  { key: ReturnTableColumns.ORDER, width: 60, hidden: false },
  { key: ReturnTableColumns.EXTERNAL_ID, width: 125, hidden: false },
  { key: ReturnTableColumns.CUSTOMER_NAME, width: 125, hidden: false },
  { key: ReturnTableColumns.SHIPPING_COUNTRY, width: 100, hidden: true },
  { key: ReturnTableColumns.ZIP_CODE, width: 90, hidden: true },
  { key: ReturnTableColumns.ITEMS_COUNT, width: 75, hidden: false },
  { key: ReturnTableColumns.DOLLAR_VALUE, width: 100, hidden: false },
  { key: ReturnTableColumns.CREATED_AT, width: 120, hidden: false },
  { key: ReturnTableColumns.TYPE, width: 125, hidden: false },
  { key: ReturnTableColumns.STATUS, width: 125, hidden: false },
  { key: ReturnTableColumns.RISK, width: 75, hidden: false },
  { key: ReturnTableColumns.SHIPMENTS, width: 125, hidden: false },
  { key: ReturnTableColumns.TAGS, width: 125, hidden: false },
];

export const ClaimTableColumns = {
  ORDER: "order",
  CUSTOMER: "customer_name",
  CREATED: "created_at",
  FILED_WITH_CARRIER: "filed_with_carrier",
  STATUS: "status",
  RISK: "risk",
  TAGS: "tags",
  EXTERNAL_ID: "external_id",
};

const defaultClaimColumns = [
  { key: ClaimTableColumns.ORDER, width: 50, hidden: false },
  { key: ClaimTableColumns.EXTERNAL_ID, width: 125, hidden: false },
  { key: ClaimTableColumns.CUSTOMER, width: 125, hidden: false },
  { key: ClaimTableColumns.CREATED, width: 75, hidden: false },
  { key: ClaimTableColumns.FILED_WITH_CARRIER, width: 75, hidden: false },
  { key: ClaimTableColumns.STATUS, width: 75, hidden: false },
  { key: ClaimTableColumns.RISK, width: 75, hidden: false },
  { key: ClaimTableColumns.TAGS, width: 125, hidden: false },
];

export const sortDefault: TableSort = {
  direction: SortDirection.DESC,
  key: ReturnTableColumns.CREATED_AT,
};

export enum ClaimSystemView {
  ALL_CLAIMS = "all",
  CREATE = "create",
  MERCHANT_REVIEW = "merchant-review",
  MANAGED = "managed",
  NEEDS_REVIEW = "needs-review",
  PARTIALLY_PROCESSED = "partially-processed",
  FAILED_RMA_CREATION = "failed-rma-creation",
  LABEL_CREATION_FAILED = "label-creation-failed",
  REFUND_PAYMENT_FAILED = "refund-payment-failed",
  TRACKING_EXCEPTION = "tracking-exception",
}

export enum WarrantySystemView {
  ALL_WARRANTIES = "all-warranties",
  CREATE = "create",
  REPAIRS = "repairs",
  NEEDS_REVIEW = "needs-review",
  PARTIALLY_PROCESSED = "partially-processed",
  FAILED_RMA_CREATION = "failed-rma-creation",
  LABEL_CREATION_FAILED = "label-creation-failed",
  REFUND_PAYMENT_FAILED = "refund-payment-failed",
  TRACKING_EXCEPTION = "tracking-exception",
}

export enum RecycleSystemView {
  ALL_RECYCLE = "all-recycle",
  CREATE = "create",
  NEEDS_REVIEW = "needs-review",
  PARTIALLY_PROCESSED = "partially-processed",
  FAILED_RMA_CREATION = "failed-rma-creation",
  LABEL_CREATION_FAILED = "label-creation-failed",
  REFUND_PAYMENT_FAILED = "refund-payment-failed",
}

export enum WithdrawalSystemView {
  ALL_WITHDRAWALS = "all-withdrawals",
  CREATE = "create",
  NEEDS_REVIEW = "needs-review",
  PARTIALLY_PROCESSED = "partially-processed",
  FAILED_RMA_CREATION = "failed-rma-creation",
  LABEL_CREATION_FAILED = "label-creation-failed",
  REFUND_PAYMENT_FAILED = "refund-payment-failed",
}

// Default columns for warranty table (similar to claims)
const defaultWarrantyColumns = [
  { key: ClaimTableColumns.ORDER, width: 50, hidden: false },
  { key: ClaimTableColumns.EXTERNAL_ID, width: 125, hidden: false },
  { key: ClaimTableColumns.CUSTOMER, width: 125, hidden: false },
  { key: ClaimTableColumns.CREATED, width: 75, hidden: false },
  { key: ClaimTableColumns.FILED_WITH_CARRIER, width: 75, hidden: false },
  { key: ClaimTableColumns.STATUS, width: 75, hidden: false },
  { key: ClaimTableColumns.RISK, width: 75, hidden: false },
  { key: ClaimTableColumns.TAGS, width: 125, hidden: false },
];

export const StaticWarrantyViews: Record<
  WarrantySystemView,
  viewTypes["warranties"]
> = {
  [WarrantySystemView.CREATE]: {
    table: "warranties",
    name: WarrantySystemView.CREATE,
    filters: [],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -1,
    showNotification: false,
    isNotEditable: true,
  },
  [WarrantySystemView.ALL_WARRANTIES]: {
    table: "warranties",
    name: WarrantySystemView.ALL_WARRANTIES,
    filters: [],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -2,
    showNotification: false,
    isNotEditable: true,
  },
  [WarrantySystemView.REPAIRS]: {
    table: "warranties",
    name: WarrantySystemView.REPAIRS,
    filters: [
      {
        name: "isRepair",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -1,
    showNotification: false,
  },
  [WarrantySystemView.NEEDS_REVIEW]: {
    table: "warranties",
    name: WarrantySystemView.NEEDS_REVIEW,
    filters: [
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: ["needs_review"],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 2,
    hideWhenEmpty: true,
    showNotification: true,
    isNotEditable: true,
  },
  [WarrantySystemView.PARTIALLY_PROCESSED]: {
    table: "warranties",
    name: WarrantySystemView.PARTIALLY_PROCESSED,
    filters: [
      {
        name: "partiallyProcessed",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 4,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WarrantySystemView.FAILED_RMA_CREATION]: {
    table: "warranties",
    name: WarrantySystemView.FAILED_RMA_CREATION,
    filters: [
      {
        name: "failedRmaCreation",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 5,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WarrantySystemView.LABEL_CREATION_FAILED]: {
    table: "warranties",
    name: WarrantySystemView.LABEL_CREATION_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [LabelCreationFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 6,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WarrantySystemView.REFUND_PAYMENT_FAILED]: {
    table: "warranties",
    name: WarrantySystemView.REFUND_PAYMENT_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [RefundPaymentFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 7,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WarrantySystemView.TRACKING_EXCEPTION]: {
    table: "warranties",
    name: WarrantySystemView.TRACKING_EXCEPTION,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [...TRACKING_EXCEPTION_TAG_NAMES],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWarrantyColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 8,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
};

const defaultRecycleColumns = [
  { key: ClaimTableColumns.ORDER, width: 50, hidden: false },
  { key: ClaimTableColumns.EXTERNAL_ID, width: 125, hidden: false },
  { key: ClaimTableColumns.CUSTOMER, width: 125, hidden: false },
  { key: ClaimTableColumns.CREATED, width: 75, hidden: false },
  { key: ClaimTableColumns.STATUS, width: 75, hidden: false },
  { key: ClaimTableColumns.RISK, width: 75, hidden: false },
  { key: ClaimTableColumns.TAGS, width: 125, hidden: false },
];

export const StaticRecycleViews: Record<
  RecycleSystemView,
  viewTypes["recycle"]
> = {
  [RecycleSystemView.CREATE]: {
    table: "recycle",
    name: RecycleSystemView.CREATE,
    filters: [],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -1,
    showNotification: false,
    isNotEditable: true,
  },
  [RecycleSystemView.ALL_RECYCLE]: {
    table: "recycle",
    name: RecycleSystemView.ALL_RECYCLE,
    filters: [],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -2,
    showNotification: false,
    isNotEditable: true,
  },
  [RecycleSystemView.NEEDS_REVIEW]: {
    table: "recycle",
    name: RecycleSystemView.NEEDS_REVIEW,
    filters: [
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: ["needs_review"],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
    ],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 2,
    hideWhenEmpty: true,
    showNotification: true,
    isNotEditable: true,
  },
  [RecycleSystemView.PARTIALLY_PROCESSED]: {
    table: "recycle",
    name: RecycleSystemView.PARTIALLY_PROCESSED,
    filters: [
      {
        name: "partiallyProcessed",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 4,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [RecycleSystemView.FAILED_RMA_CREATION]: {
    table: "recycle",
    name: RecycleSystemView.FAILED_RMA_CREATION,
    filters: [
      {
        name: "failedRmaCreation",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 5,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [RecycleSystemView.LABEL_CREATION_FAILED]: {
    table: "recycle",
    name: RecycleSystemView.LABEL_CREATION_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [LabelCreationFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 6,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [RecycleSystemView.REFUND_PAYMENT_FAILED]: {
    table: "recycle",
    name: RecycleSystemView.REFUND_PAYMENT_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [RefundPaymentFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
    ],
    columns: defaultRecycleColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 7,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
};

export const StaticClaimViews: Record<ClaimSystemView, viewTypes["claims"]> = {
  [ClaimSystemView.CREATE]: {
    table: "claims",
    name: ClaimSystemView.CREATE,
    filters: [],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -1,
    showNotification: false,
    isNotEditable: true,
  },
  [ClaimSystemView.ALL_CLAIMS]: {
    table: "claims",
    name: ClaimSystemView.ALL_CLAIMS,
    filters: [],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -2,
    showNotification: false,
    isNotEditable: true,
  },
  [ClaimSystemView.MERCHANT_REVIEW]: {
    table: "claims",
    name: ClaimSystemView.MERCHANT_REVIEW,
    filters: [
      {
        name: "reviewingParty",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
    showNotification: false,
  },
  [ClaimSystemView.MANAGED]: {
    table: "claims",
    name: ClaimSystemView.MANAGED,
    filters: [
      {
        name: "reviewingParty",
        type: AdvancedFilterType.BOOLEAN,
        value: false,
        operator: BooleanFilterOperator.IS,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 1,
    showNotification: false,
  },
  [ClaimSystemView.NEEDS_REVIEW]: {
    table: "claims",
    name: ClaimSystemView.NEEDS_REVIEW,
    filters: [
      {
        name: "reviewingParty",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: ["needs_review"],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 2,
    hideWhenEmpty: true,
    showNotification: true,
    isNotEditable: true,
  },
  [ClaimSystemView.PARTIALLY_PROCESSED]: {
    table: "claims",
    name: ClaimSystemView.PARTIALLY_PROCESSED,
    filters: [
      {
        name: "partiallyProcessed",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 4,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [ClaimSystemView.FAILED_RMA_CREATION]: {
    table: "claims",
    name: ClaimSystemView.FAILED_RMA_CREATION,
    filters: [
      {
        name: "failedRmaCreation",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 5,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [ClaimSystemView.LABEL_CREATION_FAILED]: {
    table: "claims",
    name: ClaimSystemView.LABEL_CREATION_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [LabelCreationFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 6,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [ClaimSystemView.REFUND_PAYMENT_FAILED]: {
    table: "claims",
    name: ClaimSystemView.REFUND_PAYMENT_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [RefundPaymentFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 7,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [ClaimSystemView.TRACKING_EXCEPTION]: {
    table: "claims",
    name: ClaimSystemView.TRACKING_EXCEPTION,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [...TRACKING_EXCEPTION_TAG_NAMES],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultClaimColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 8,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
};

export const PathReservedNames = [
  "create-return",
  "create-claim",
  "managed",
  "registrations",
  "create-warranty-claim",
  "about",
];

export enum ReturnSystemView {
  OPEN = "open",
  IN_TRANSIT = "in-transit",
  STUCK_IN_TRANSIT = "stuck-in-transit",
  AWAITING_PROCESSING = "awaiting-processing",
  NEEDS_REVIEW = "needs-review",
  FLAGGED = "flagged",
  FRAUD_RISK = "fraud-risk",
  OUTSTANDING_BALANCE = "outstanding-balance",
  PARTIALLY_PROCESSED = "partially-processed",
  FAILED_RMA_CREATION = "failed-rma-creation",
  LABEL_CREATION_FAILED = "label-creation-failed",
  REFUND_PAYMENT_FAILED = "refund-payment-failed",
  RESTOCK_FAILED = "restock-failed",
  OUT_OF_STOCK = "out-of-stock",
  TRACKING_EXCEPTION = "tracking-exception",
  CREATE = "create",
  ALL_RETURNS = "all-returns",
}

export const ReturnSystemViewsNoCreate: string[] = [
  ReturnSystemView.CREATE,
  ReturnSystemView.ALL_RETURNS,
  ClaimSystemView.ALL_CLAIMS,
  WarrantySystemView.ALL_WARRANTIES,
  RecycleSystemView.ALL_RECYCLE,
  WithdrawalSystemView.ALL_WITHDRAWALS,
];

export const StaticSystemViews: Record<ReturnSystemView, viewTypes["returns"]> =
  {
    [ReturnSystemView.OPEN]: {
      table: "returns",
      name: ReturnSystemView.OPEN,
      filters: [
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: ["open"],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 0,
      showNotification: true,
    },
    [ReturnSystemView.IN_TRANSIT]: {
      table: "returns",
      name: ReturnSystemView.IN_TRANSIT,
      filters: [
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: ["in_transit"],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 1,
      showNotification: false,
    },
    [ReturnSystemView.STUCK_IN_TRANSIT]: {
      table: "returns",
      name: ReturnSystemView.STUCK_IN_TRANSIT,
      filters: [
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.COMPLETE,
            ReturnStatus.REJECTED,
            ReturnStatus.DELETED,
          ],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
        {
          name: "shipmentStatus",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ShipmentTrackerStatus.IN_TRANSIT,
            ShipmentTrackerStatus.OUT_FOR_DELIVERY,
            ShipmentTrackerStatus.ERROR,
            ShipmentTrackerStatus.FAILURE,
          ],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
        {
          name: "shipmentDaysSinceLastUpdate",
          type: AdvancedFilterType.NUMBER,
          value: [10],
          operator: NumberFilterOperator.GREATER_THAN,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 2,
      showNotification: true,
      hideWhenEmpty: true,
    },
    [ReturnSystemView.AWAITING_PROCESSING]: {
      table: "returns",
      name: ReturnSystemView.AWAITING_PROCESSING,
      filters: [
        {
          name: "createdAt",
          type: AdvancedFilterType.DATE,
          value: KnownDateFilterTimeFrame.RELATIVE,
          operator: DateFilterOperator.WITHIN,
          relativeTime: { value: 60, unit: RelativeTimeUnit.DAYS },
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.DELIVERED,
            ReturnStatus.OPEN,
            ReturnStatus.NEEDS_REVIEW,
          ],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
        {
          name: "shipmentStatus",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [ShipmentTrackerStatus.DELIVERED],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
        {
          name: "shipmentDaysSinceLastUpdate",
          type: AdvancedFilterType.NUMBER,
          value: [10],
          operator: NumberFilterOperator.GREATER_THAN,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 3,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.NEEDS_REVIEW]: {
      table: "returns",
      name: ReturnSystemView.NEEDS_REVIEW,
      filters: [
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: ["needs_review"],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 4,
      hideWhenEmpty: true,
      showNotification: true,
      isNotEditable: true,
    },
    [ReturnSystemView.FLAGGED]: {
      table: "returns",
      name: ReturnSystemView.FLAGGED,
      filters: [
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: ["flagged"],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 5,
      showNotification: true,
    },
    [ReturnSystemView.FRAUD_RISK]: {
      table: "returns",
      name: ReturnSystemView.FRAUD_RISK,
      filters: [
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: ["flagged"],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [FraudRiskTag.name],
          operator: ArrayStringFilterOperator.ALL_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 6,
      hideWhenEmpty: true,
      showNotification: true,
    },
    [ReturnSystemView.OUTSTANDING_BALANCE]: {
      table: "returns",
      name: ReturnSystemView.OUTSTANDING_BALANCE,
      filters: [
        {
          name: "exchangePaymentStatus",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: ["pending"],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            "open",
            "in_transit",
            "delivered",
            "needs_review",
            "in_review",
            "flagged",
          ],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 7,
      showNotification: true,
    },
    [ReturnSystemView.PARTIALLY_PROCESSED]: {
      table: "returns",
      name: ReturnSystemView.PARTIALLY_PROCESSED,
      filters: [
        {
          name: "partiallyProcessed",
          type: AdvancedFilterType.BOOLEAN,
          value: true,
          operator: BooleanFilterOperator.IS,
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.COMPLETE,
            ReturnStatus.REJECTED,
            ReturnStatus.DELETED,
          ],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [OutOfStockTag.name],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 8,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.FAILED_RMA_CREATION]: {
      table: "returns",
      name: ReturnSystemView.FAILED_RMA_CREATION,
      filters: [
        {
          name: "failedRmaCreation",
          type: AdvancedFilterType.BOOLEAN,
          value: true,
          operator: BooleanFilterOperator.IS,
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.COMPLETE,
            ReturnStatus.REJECTED,
            ReturnStatus.DELETED,
          ],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 9,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.LABEL_CREATION_FAILED]: {
      table: "returns",
      name: ReturnSystemView.LABEL_CREATION_FAILED,
      filters: [
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [LabelCreationFailedTag.name],
          operator: ArrayStringFilterOperator.ALL_OF,
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.COMPLETE,
            ReturnStatus.REJECTED,
            ReturnStatus.DELETED,
          ],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 10,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.REFUND_PAYMENT_FAILED]: {
      table: "returns",
      name: ReturnSystemView.REFUND_PAYMENT_FAILED,
      filters: [
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [RefundPaymentFailedTag.name],
          operator: ArrayStringFilterOperator.ALL_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 11,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.RESTOCK_FAILED]: {
      table: "returns",
      name: ReturnSystemView.RESTOCK_FAILED,
      filters: [
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [RestockFailedTag.name],
          operator: ArrayStringFilterOperator.ALL_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 12,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.OUT_OF_STOCK]: {
      table: "returns",
      name: ReturnSystemView.OUT_OF_STOCK,
      filters: [
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [OutOfStockTag.name],
          operator: ArrayStringFilterOperator.ALL_OF,
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.COMPLETE,
            ReturnStatus.REJECTED,
            ReturnStatus.DELETED,
          ],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 13,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.TRACKING_EXCEPTION]: {
      table: "returns",
      name: ReturnSystemView.TRACKING_EXCEPTION,
      filters: [
        {
          name: "tags",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [...TRACKING_EXCEPTION_TAG_NAMES],
          operator: ArrayStringFilterOperator.ANY_OF,
        },
        {
          name: "status",
          type: AdvancedFilterType.ARRAY_TO_STRING,
          value: [
            ReturnStatus.COMPLETE,
            ReturnStatus.REJECTED,
            ReturnStatus.DELETED,
          ],
          operator: ArrayStringFilterOperator.NONE_OF,
        },
      ],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: 14,
      showNotification: true,
      hideWhenEmpty: true,
      isNotEditable: true,
    },
    [ReturnSystemView.CREATE]: {
      table: "returns",
      name: ReturnSystemView.CREATE,
      filters: [],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: -1,
      showNotification: false,
      isNotEditable: true,
    },
    [ReturnSystemView.ALL_RETURNS]: {
      table: "returns",
      name: ReturnSystemView.ALL_RETURNS,
      filters: [],
      columns: defaultReturnColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: sortDefault,
      position: -2,
      showNotification: false,
      isNotEditable: true,
    },
  };

enum DeprecatedViewNames {
  DELIVERED = "delivered",
  COMPLETE = "complete",
  REJECTED = "rejected",
  RESET = "reset",
}

export function friendlyViewName(
  view:
    | viewTypes["returns"]
    | viewTypes["claims"]
    | viewTypes["warranties"]
    | viewTypes["recycle"]
    | viewTypes["withdrawals"],
) {
  switch (view.name) {
    case ReturnSystemView.OPEN:
      return "Open";
    case ReturnSystemView.IN_TRANSIT:
      return "In transit";
    case ReturnSystemView.STUCK_IN_TRANSIT:
      return "Stuck in transit";
    case ReturnSystemView.AWAITING_PROCESSING:
      return "Awaiting processing";
    case ReturnSystemView.NEEDS_REVIEW:
      return "Manual review";
    case ReturnSystemView.FLAGGED:
      return "Flagged";
    case ReturnSystemView.FRAUD_RISK:
      return "Fraud Risk";
    case ReturnSystemView.OUTSTANDING_BALANCE:
      return "Outstanding balance";
    case ReturnSystemView.PARTIALLY_PROCESSED:
      return "Partially processed";
    case ReturnSystemView.FAILED_RMA_CREATION:
      return "Failed RMA creation";
    case ReturnSystemView.LABEL_CREATION_FAILED:
      return "Label creation failed";
    case ReturnSystemView.REFUND_PAYMENT_FAILED:
      return "Refund payment failed";
    case ReturnSystemView.RESTOCK_FAILED:
      return "Restock failed";
    case ReturnSystemView.OUT_OF_STOCK:
      return "Out of stock";
    case ReturnSystemView.TRACKING_EXCEPTION:
      return "Tracking exception";
    case ReturnSystemView.ALL_RETURNS:
      return "All returns";
    case ClaimSystemView.ALL_CLAIMS:
      return "All claims";
    case ClaimSystemView.MERCHANT_REVIEW:
      return "Merchant review";
    case ClaimSystemView.MANAGED:
      return "Managed";
    case ClaimSystemView.NEEDS_REVIEW:
      return "Manual review";
    case ClaimSystemView.PARTIALLY_PROCESSED:
      return "Partially processed";
    case ClaimSystemView.FAILED_RMA_CREATION:
      return "Failed RMA creation";
    case ClaimSystemView.LABEL_CREATION_FAILED:
      return "Label creation failed";
    case ClaimSystemView.TRACKING_EXCEPTION:
      return "Tracking exception";
    case WarrantySystemView.ALL_WARRANTIES:
      return "All warranties";
    case WarrantySystemView.REPAIRS:
      return "Repairs";
    case WarrantySystemView.NEEDS_REVIEW:
      return "Manual review";
    case WarrantySystemView.PARTIALLY_PROCESSED:
      return "Partially processed";
    case WarrantySystemView.FAILED_RMA_CREATION:
      return "Failed RMA creation";
    case WarrantySystemView.LABEL_CREATION_FAILED:
      return "Label creation failed";
    case WarrantySystemView.TRACKING_EXCEPTION:
      return "Tracking exception";
    case RecycleSystemView.ALL_RECYCLE:
      return "All recycle";
    case RecycleSystemView.NEEDS_REVIEW:
      return "Manual review";
    case RecycleSystemView.PARTIALLY_PROCESSED:
      return "Partially processed";
    case RecycleSystemView.FAILED_RMA_CREATION:
      return "Failed RMA creation";
    case RecycleSystemView.LABEL_CREATION_FAILED:
      return "Label creation failed";
    case WithdrawalSystemView.ALL_WITHDRAWALS:
      return "All withdrawals";
    case DeprecatedViewNames.RESET:
      return "Reset";
    case DeprecatedViewNames.COMPLETE:
      return "Complete";
    case DeprecatedViewNames.REJECTED:
      return "Rejected";
    case DeprecatedViewNames.DELIVERED:
      return "Delivered";
    default:
      return view.name;
  }
}

export const NEEDS_ATTENTION_FOLDER_NAME = "Needs attention";

export const DEFAULT_RETURN_FOLDERS = [
  {
    name: NEEDS_ATTENTION_FOLDER_NAME,
    viewNames: [
      ReturnSystemView.STUCK_IN_TRANSIT,
      ReturnSystemView.AWAITING_PROCESSING,
      ReturnSystemView.PARTIALLY_PROCESSED,
      ReturnSystemView.FAILED_RMA_CREATION,
      ReturnSystemView.LABEL_CREATION_FAILED,
      ReturnSystemView.REFUND_PAYMENT_FAILED,
      ReturnSystemView.RESTOCK_FAILED,
      ReturnSystemView.OUT_OF_STOCK,
      ReturnSystemView.TRACKING_EXCEPTION,
    ],
  },
];

export const DEFAULT_CLAIM_FOLDERS = [
  {
    name: NEEDS_ATTENTION_FOLDER_NAME,
    viewNames: [
      ClaimSystemView.PARTIALLY_PROCESSED,
      ClaimSystemView.FAILED_RMA_CREATION,
      ClaimSystemView.LABEL_CREATION_FAILED,
      ClaimSystemView.REFUND_PAYMENT_FAILED,
      ClaimSystemView.TRACKING_EXCEPTION,
    ],
  },
];

export const DEFAULT_WARRANTY_FOLDERS = [
  {
    name: NEEDS_ATTENTION_FOLDER_NAME,
    viewNames: [
      WarrantySystemView.PARTIALLY_PROCESSED,
      WarrantySystemView.FAILED_RMA_CREATION,
      WarrantySystemView.LABEL_CREATION_FAILED,
      WarrantySystemView.REFUND_PAYMENT_FAILED,
      WarrantySystemView.TRACKING_EXCEPTION,
    ],
  },
];

export const DEFAULT_RECYCLE_FOLDERS = [
  {
    name: NEEDS_ATTENTION_FOLDER_NAME,
    viewNames: [
      RecycleSystemView.PARTIALLY_PROCESSED,
      RecycleSystemView.FAILED_RMA_CREATION,
      RecycleSystemView.LABEL_CREATION_FAILED,
      RecycleSystemView.REFUND_PAYMENT_FAILED,
    ],
  },
];

const defaultWithdrawalColumns = [
  { key: ClaimTableColumns.ORDER, width: 50, hidden: false },
  { key: ClaimTableColumns.EXTERNAL_ID, width: 125, hidden: false },
  { key: ClaimTableColumns.CUSTOMER, width: 125, hidden: false },
  { key: ClaimTableColumns.CREATED, width: 75, hidden: false },
  { key: ClaimTableColumns.STATUS, width: 75, hidden: false },
  { key: ClaimTableColumns.RISK, width: 75, hidden: false },
  { key: ClaimTableColumns.TAGS, width: 125, hidden: false },
];

export const StaticWithdrawalViews: Record<
  WithdrawalSystemView,
  viewTypes["withdrawals"]
> = {
  [WithdrawalSystemView.CREATE]: {
    table: "withdrawals",
    name: WithdrawalSystemView.CREATE,
    filters: [],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -1,
    showNotification: false,
    isNotEditable: true,
  },
  [WithdrawalSystemView.ALL_WITHDRAWALS]: {
    table: "withdrawals",
    name: WithdrawalSystemView.ALL_WITHDRAWALS,
    filters: [],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -2,
    showNotification: false,
    isNotEditable: true,
  },
  [WithdrawalSystemView.NEEDS_REVIEW]: {
    table: "withdrawals",
    name: WithdrawalSystemView.NEEDS_REVIEW,
    filters: [
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: ["needs_review"],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
    ],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 2,
    hideWhenEmpty: true,
    showNotification: true,
    isNotEditable: true,
  },
  [WithdrawalSystemView.PARTIALLY_PROCESSED]: {
    table: "withdrawals",
    name: WithdrawalSystemView.PARTIALLY_PROCESSED,
    filters: [
      {
        name: "partiallyProcessed",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 4,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WithdrawalSystemView.FAILED_RMA_CREATION]: {
    table: "withdrawals",
    name: WithdrawalSystemView.FAILED_RMA_CREATION,
    filters: [
      {
        name: "failedRmaCreation",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 5,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WithdrawalSystemView.LABEL_CREATION_FAILED]: {
    table: "withdrawals",
    name: WithdrawalSystemView.LABEL_CREATION_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [LabelCreationFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [
          ReturnStatus.COMPLETE,
          ReturnStatus.REJECTED,
          ReturnStatus.DELETED,
        ],
        operator: ArrayStringFilterOperator.NONE_OF,
      },
    ],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 6,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
  [WithdrawalSystemView.REFUND_PAYMENT_FAILED]: {
    table: "withdrawals",
    name: WithdrawalSystemView.REFUND_PAYMENT_FAILED,
    filters: [
      {
        name: "tags",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [RefundPaymentFailedTag.name],
        operator: ArrayStringFilterOperator.ALL_OF,
      },
    ],
    columns: defaultWithdrawalColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 7,
    showNotification: true,
    hideWhenEmpty: true,
    isNotEditable: true,
  },
};

export const DEFAULT_WITHDRAWAL_FOLDERS = [
  {
    name: NEEDS_ATTENTION_FOLDER_NAME,
    viewNames: [
      WithdrawalSystemView.PARTIALLY_PROCESSED,
      WithdrawalSystemView.FAILED_RMA_CREATION,
      WithdrawalSystemView.LABEL_CREATION_FAILED,
      WithdrawalSystemView.REFUND_PAYMENT_FAILED,
    ],
  },
];
